import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import { normalizeProjectRef } from '../lib/project-ref';
import { pmFetch, pmRetry } from './api';
import { pmKeys } from './keys';
import type {
  PmAccessItem, PmActivityPage, PmAdminUser, PmDeadlineFilters, PmDeadlineListItem, PmImportRun, PmImportRunDetail, PmLookups,
  PmMe, PmMySettings, PmNotesPage, PmPerson, PmPersonAdmin, PmProjectDetail, PmProjectFilters, PmProjectListResponse, PmTaskFilters,
  PmTaskListItem, PmTimeline, PmTimelineNotes, PmTimelineParams,
} from './types';

export function usePmMe() {
  return useQuery({
    queryKey: pmKeys.me,
    queryFn: ({ signal }) => pmFetch<PmMe>('/me', { signal }),
    staleTime: 60_000,
    retry: pmRetry,
  });
}

/** v1.3 GET /me/settings (login only). A failure is treated as "no saved settings" by the caller (never a toast). */
export function usePmMySettings() {
  return useQuery({
    queryKey: pmKeys.settings,
    queryFn: ({ signal }) => pmFetch<PmMySettings>('/me/settings', { signal }),
    staleTime: Infinity,
    retry: false,
  });
}

export function usePmLookups() {
  return useQuery({
    queryKey: pmKeys.lookups,
    queryFn: ({ signal }) => pmFetch<PmLookups>('/lookups', { signal }),
    staleTime: 5 * 60_000,
    retry: pmRetry,
  });
}

export function usePmPeople(includeInactive = false) {
  return useQuery({
    queryKey: pmKeys.people(includeInactive),
    queryFn: ({ signal }) => pmFetch<{ people: PmPerson[] }>('/people', { query: { includeInactive }, signal }),
    retry: pmRetry,
  });
}

export function usePmProjects(filters: PmProjectFilters, { enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: pmKeys.projectList(filters),
    queryFn: ({ signal }) => pmFetch<PmProjectListResponse>('/projects', { query: { ...filters }, signal }),
    enabled,
    retry: pmRetry,
  });
}

/**
 * GET /projects/:ref — `ref` is a code (any case) or a numeric id (code-less projects, CONTRACT 18.1.1). The answer is also
 * written under the project's other key (id <-> code) so both URLs share one cache entry's data.
 */
export function usePmProject(ref: string | undefined) {
  const qc = useQueryClient();
  const norm = normalizeProjectRef(ref ?? '');
  return useQuery({
    queryKey: pmKeys.project(norm),
    queryFn: async ({ signal }) => {
      const detail = await pmFetch<PmProjectDetail>('/projects/' + encodeURIComponent(norm), { signal });
      for (const alias of [String(detail.id), detail.code]) {
        if (alias !== null && normalizeProjectRef(alias) !== norm) qc.setQueryData(pmKeys.project(alias), detail);
      }
      return detail;
    },
    enabled: !!norm,
    retry: pmRetry,
  });
}

export function usePmActivity(projectId: number | undefined) {
  return useInfiniteQuery({
    queryKey: pmKeys.activity(projectId ?? 0),
    queryFn: ({ pageParam, signal }) =>
      pmFetch<PmActivityPage>(`/projects/${projectId}/activity`, { query: { limit: 50, before: pageParam }, signal }),
    initialPageParam: undefined as number | undefined,
    getNextPageParam: (last) => last.nextBefore ?? undefined,
    enabled: projectId !== undefined,
    retry: pmRetry,
  });
}

export function usePmNotes(projectId: number | undefined) {
  return useInfiniteQuery({
    queryKey: pmKeys.notes(projectId ?? 0),
    queryFn: ({ pageParam, signal }) =>
      pmFetch<PmNotesPage>(`/projects/${projectId}/notes`, { query: { limit: 20, cursor: pageParam }, signal }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled: projectId !== undefined,
    retry: pmRetry,
  });
}

/** v1.2: notes of one phase (newest first, 20 per page) for the phase panel's "Updates" section. */
export function usePhaseNotes(projectId: number | undefined, phaseId: number | undefined, enabled = true) {
  return useInfiniteQuery({
    queryKey: pmKeys.phaseNotes(projectId ?? 0, phaseId ?? 0),
    queryFn: ({ pageParam, signal }) =>
      pmFetch<PmNotesPage>(`/projects/${projectId}/notes`, { query: { limit: 20, cursor: pageParam, phaseId }, signal }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled: enabled && projectId !== undefined && phaseId !== undefined,
    retry: pmRetry,
  });
}

export function usePmTasks(filters: PmTaskFilters, { enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: pmKeys.tasks(filters),
    queryFn: ({ signal }) => pmFetch<{ items: PmTaskListItem[] }>('/tasks', { query: { ...filters }, signal }),
    enabled,
    retry: pmRetry,
  });
}

export function usePmDeadlines(filters: PmDeadlineFilters, { enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: pmKeys.deadlines(filters),
    queryFn: ({ signal }) => pmFetch<{ items: PmDeadlineListItem[] }>('/deadlines', { query: { ...filters }, signal }),
    enabled,
    retry: pmRetry,
  });
}

export function usePmTimeline(params: PmTimelineParams, { enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: pmKeys.timeline(params),
    queryFn: ({ signal }) => pmFetch<PmTimeline>('/timeline', { query: { ...params }, signal }),
    enabled,
    retry: pmRetry,
  });
}

/** v1.2 GET /timeline/notes: note excerpts for the Follow-up popovers. Only for PM users (`me.canAccess`). */
export function useTimelineNotes(params: PmTimelineParams, enabled: boolean) {
  return useQuery({
    queryKey: pmKeys.timelineNotes(params),
    queryFn: ({ signal }) => pmFetch<PmTimelineNotes>('/timeline/notes', { query: { ...params }, signal }),
    enabled,
    retry: pmRetry,
  });
}

export function usePmAdminAccess() {
  return useQuery({
    queryKey: pmKeys.admin.access,
    queryFn: ({ signal }) => pmFetch<{ items: PmAccessItem[] }>('/admin/access', { signal }),
    retry: pmRetry,
  });
}

export function usePmAdminUsers() {
  return useQuery({
    queryKey: pmKeys.admin.users,
    queryFn: ({ signal }) => pmFetch<{ items: PmAdminUser[] }>('/admin/users', { signal }),
    retry: pmRetry,
  });
}

export function usePmAdminPeople() {
  return useQuery({
    queryKey: pmKeys.admin.people,
    queryFn: ({ signal }) => pmFetch<{ items: PmPersonAdmin[] }>('/admin/people', { signal }),
    retry: pmRetry,
  });
}

export function usePmImportRuns() {
  return useQuery({
    queryKey: pmKeys.admin.importRuns,
    queryFn: ({ signal }) => pmFetch<{ items: PmImportRun[] }>('/admin/import-runs', { signal }),
    retry: pmRetry,
  });
}

export function usePmImportRun(id: number | undefined) {
  return useQuery({
    queryKey: pmKeys.admin.importRun(id ?? 0),
    queryFn: ({ signal }) => pmFetch<PmImportRunDetail>(`/admin/import-runs/${id}`, { signal }),
    enabled: id !== undefined,
    retry: pmRetry,
  });
}
